import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Wind, ArrowRight, ArrowDown, Shield, Cpu, BarChart3, ChevronRight, Building2, CheckCircle2, Layers, Zap } from 'lucide-react';
import { AirParticles } from '@/components/Layout';
import AirFlowSimulator from '@/components/AirFlowSimulator';
import VayuPaneViewer from '@/components/VayuPaneViewer';
import logger from '@/utils/logger';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAqiColor(aqi) {
  if (aqi <= 50) return '#4ADE80';
  if (aqi <= 100) return '#FBBF24';
  if (aqi <= 150) return '#FB923C';
  if (aqi <= 200) return '#F87171';
  return '#7F1D1D';
}

// Scroll reveal hook
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { el.classList.add('revealed'); observer.unobserve(el); }
    }, { threshold: 0.12 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function SR({ children, className = '', delay = 0 }) {
  const ref = useReveal();
  return <div ref={ref} className={`sr ${delay ? `sr-delay-${delay}` : ''} ${className}`}>{children}</div>;
}

// Animated counter
function CountUp({ target, suffix = '', prefix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started) { setStarted(true); observer.unobserve(el); }
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const steps = 60;
    const inc = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += inc;
      if (current >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.round(current));
    }, duration / steps);
    return () => clearInterval(timer);
  }, [started, target, duration]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
}

// ===== 1. HERO =====
function Hero({ onBookDemo }) {
  const navigate = useNavigate();
  return (
    <section className="relative min-h-screen overflow-hidden section-dark" data-testid="hero-section">
      <AirParticles count={30} color="rgba(63,167,214,0.1)" />
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 65% 40%, rgba(63,167,214,0.08) 0%, transparent 65%)' }} />

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-20 pt-36 pb-24 min-h-screen flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-center w-full">
          <div className="lg:col-span-2">
            <SR delay={1}>
              <h1 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-bold leading-[1.08] tracking-tight mb-6" style={{ fontFamily: 'Inter', color: 'var(--text-light)' }} data-testid="hero-headline">
                The future of air<br />starts at the<br /><span style={{ color: 'var(--aqua)' }}>window.</span>
              </h1>
            </SR>
            <SR delay={2}>
              <p className="text-base leading-relaxed mb-3" style={{ color: 'var(--text-secondary-light)' }}>
                Vayu is the first air infrastructure system that purifies air as it enters buildings.
              </p>
              <p className="text-sm mb-10" style={{ color: 'var(--text-muted)' }}>Built by Jagdale Air</p>
            </SR>
            <SR delay={3}>
              <div className="flex flex-wrap gap-4">
                <button onClick={() => navigate('/product')} className="btn-glow flex items-center gap-2" data-testid="hero-explore-btn">
                  Explore Vayu <ArrowRight className="w-4 h-4" />
                </button>
                <a href="#problem" className="btn-ghost flex items-center gap-2" data-testid="hero-how-btn">
                  See How It Works <ArrowDown className="w-4 h-4" />
                </a>
              </div>
            </SR>
          </div>

          <div className="lg:col-span-3 relative" data-testid="hero-visual">
            <SR delay={3}>
              <div className="relative rounded-3xl overflow-hidden" style={{ border: '1px solid rgba(63,167,214,0.1)', boxShadow: '0 0 80px rgba(63,167,214,0.06)' }}>
                <div className="grid grid-cols-2">
                  <div className="relative h-72 md:h-96">
                    <img src="https://images.unsplash.com/photo-1686840007773-67280673ca0b?w=600&q=70" alt="Polluted city" className="w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(6,26,31,0.95) 0%, rgba(248,113,113,0.1) 50%, rgba(6,26,31,0.5) 100%)' }} />
                    <div className="absolute bottom-6 left-6">
                      <p className="text-[10px] uppercase tracking-[0.2em] mb-1" style={{ color: 'var(--warning)' }}>Outside</p>
                      <p className="stat-huge text-4xl text-white">AQI 156</p>
                      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Unhealthy</p>
                    </div>
                  </div>
                  <div className="relative h-72 md:h-96">
                    <img src="https://images.unsplash.com/photo-1758315417336-38777d626815?w=600&q=70" alt="Clean indoor" className="w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(6,26,31,0.95) 0%, rgba(74,222,128,0.05) 50%, rgba(6,26,31,0.3) 100%)' }} />
                    <div className="absolute bottom-6 left-6">
                      <p className="text-[10px] uppercase tracking-[0.2em] mb-1" style={{ color: 'var(--success)' }}>With Vayu</p>
                      <p className="stat-huge text-4xl text-white">AQI 18</p>
                      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Excellent</p>
                    </div>
                  </div>
                </div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 px-4 py-2 rounded-full z-10" style={{ background: 'var(--bg-deep)', border: '1px solid rgba(63,167,214,0.2)', boxShadow: '0 0 30px rgba(63,167,214,0.15)' }}>
                  <span className="text-xs font-bold" style={{ color: 'var(--aqua)' }}>88% reduction</span>
                </div>
              </div>
            </SR>
          </div>
        </div>
      </div>
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="text-[9px] uppercase tracking-[0.3em]" style={{ color: 'var(--text-muted)' }}>Scroll</span>
        <div className="w-px h-10" style={{ background: 'linear-gradient(to bottom, var(--text-muted), transparent)' }} />
      </div>
    </section>
  );
}

// ===== 2. PROBLEM =====
function Problem() {
  return (
    <section id="problem" className="section-gap relative overflow-hidden" style={{ background: 'linear-gradient(180deg, var(--bg-deep) 0%, #0A1215 100%)' }} data-testid="problem-section">
      <div className="absolute inset-0 haze-bg" style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(248,113,113,0.04) 0%, transparent 70%)' }} />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-20 relative z-10">
        <SR><p className="overline-glow text-center mb-6">The Problem</p></SR>
        <SR delay={1}><h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-center mb-8" style={{ fontFamily: 'Inter' }} data-testid="problem-headline">Air is broken.</h2></SR>
        <SR delay={2}><p className="text-center text-base lg:text-lg leading-relaxed max-w-2xl mx-auto mb-16" style={{ color: 'var(--text-secondary-light)' }}>
          Opening your window brings pollution inside.<br />Closing it traps stale air.<br /><br />Buildings today are not designed to manage air.<br />They simply block it — or let it in.
        </p></SR>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          <SR delay={3}>
            <div className="card-dark p-8" data-testid="problem-card-outside">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(248,113,113,0.1)' }}>
                <Wind className="w-5 h-5" style={{ color: 'var(--warning)' }} strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Inter' }}>Window Open</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary-light)' }}>PM2.5, vehicle exhaust, industrial pollutants — all flowing directly into your home.</p>
            </div>
          </SR>
          <SR delay={4}>
            <div className="card-dark p-8" data-testid="problem-card-inside">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(251,191,36,0.1)' }}>
                <Layers className="w-5 h-5" style={{ color: '#FBBF24' }} strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Inter' }}>Window Closed</h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary-light)' }}>CO2 builds up, humidity rises, stale air circulates. Your space suffocates silently.</p>
            </div>
          </SR>
        </div>
      </div>
    </section>
  );
}

// ===== 3. BREAKTHROUGH =====
function Breakthrough() {
  return (
    <section className="section-gap section-dark relative overflow-hidden" data-testid="breakthrough-section">
      <AirParticles count={12} color="rgba(63,167,214,0.06)" />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-20 text-center relative z-10">
        <SR><p className="overline-glow mb-6">The Breakthrough</p></SR>
        <SR delay={1}>
          <h2 className="text-4xl md:text-5xl lg:text-[4rem] font-bold tracking-tight mb-6" style={{ fontFamily: 'Inter' }}>
            Introducing <span style={{ color: 'var(--aqua)' }}>Vayu</span>
          </h2>
        </SR>
        <SR delay={2}><p className="text-xl md:text-2xl font-light leading-relaxed max-w-2xl mx-auto mb-4" style={{ color: 'var(--text-secondary-light)' }}>A new way for buildings to breathe.</p></SR>
        <SR delay={3}><p className="text-base leading-relaxed max-w-xl mx-auto" style={{ color: 'var(--text-muted)' }}>Vayu delivers purified, intelligent airflow directly into your living space — at the point of entry.</p></SR>
      </div>
    </section>
  );
}

// ===== 4. SOLUTION =====
function Solution() {
  const navigate = useNavigate();
  const products = [
    { id: 'pane', name: 'Vayu Pane', tag: 'Smart Window', desc: 'Filters air at the point of entry. The first line of defense.', icon: Shield, color: 'var(--aqua)' },
    { id: 'pure', name: 'Vayu Pure', tag: 'Air Purifier', desc: 'Medical-grade indoor purification. Works in harmony with Pane.', icon: Wind, color: 'var(--success)' },
    { id: 'sense', name: 'Vayu Sense', tag: 'Intelligence', desc: 'Monitors, analyzes, and orchestrates your entire air system.', icon: Cpu, color: '#A78BFA' },
  ];
  return (
    <section className="section-gap section-surface" data-testid="solution-section">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-20">
        <SR><p className="overline-glow text-center mb-6">The Vayu Ecosystem</p></SR>
        <SR delay={1}><h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center mb-16" style={{ fontFamily: 'Inter' }}>Three products. One system.</h2></SR>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {products.map((p, i) => (
            <SR key={p.id} delay={i + 2}>
              <div className="card-dark p-8 h-full" data-testid={`solution-card-${p.id}`}>
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6" style={{ background: `${p.color}10` }}>
                  <p.icon className="w-5 h-5" style={{ color: p.color }} strokeWidth={1.5} />
                </div>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] mb-1" style={{ color: p.color }}>{p.tag}</p>
                <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Inter' }}>{p.name}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary-light)' }}>{p.desc}</p>
                <button onClick={() => navigate('/product')} className="mt-6 inline-flex items-center gap-1 text-sm font-medium" style={{ color: p.color }} data-testid={`explore-${p.id}`}>
                  Learn More <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </SR>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===== 5. HOW IT WORKS =====
function HowItWorks() {
  const steps = [
    { num: '01', title: 'Intake', desc: 'Air enters through Vayu Pane', color: 'var(--warning)' },
    { num: '02', title: 'Filtration', desc: 'Multi-stage HEPA removes 99.5% pollutants', color: 'var(--aqua)' },
    { num: '03', title: 'Optimization', desc: 'AI adjusts flow based on real-time AQI', color: '#A78BFA' },
    { num: '04', title: 'Delivery', desc: 'Clean air flows into your space', color: 'var(--success)' },
  ];
  const lineRef = useReveal();
  return (
    <section className="section-gap section-dark" data-testid="how-it-works-section">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-20">
        <SR><p className="overline-glow text-center mb-6">How It Works</p></SR>
        <SR delay={1}><h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center mb-16" style={{ fontFamily: 'Inter' }}>From polluted to pure. In seconds.</h2></SR>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          {steps.map((s, i) => (
            <SR key={s.num} delay={i + 1}>
              <div className="text-center" data-testid={`step-${s.num}`}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: `${s.color}10`, border: `1px solid ${s.color}20` }}>
                  <span className="stat-huge text-xl" style={{ color: s.color }}>{s.num}</span>
                </div>
                <h3 className="text-base font-semibold mb-1" style={{ fontFamily: 'Inter' }}>{s.title}</h3>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{s.desc}</p>
              </div>
            </SR>
          ))}
        </div>
        <div ref={lineRef} className="sr max-w-2xl mx-auto">
          <div className="h-0.5 rounded-full" style={{ background: 'linear-gradient(to right, var(--warning), var(--aqua), #A78BFA, var(--success))' }} />
        </div>
      </div>
    </section>
  );
}

// ===== 6. WITH vs WITHOUT =====
function WithVsWithout() {
  const [sliderPos, setSliderPos] = useState(50);
  const [aqiData, setAqiData] = useState({ outdoor: 180, indoor: 22 });
  const containerRef = useRef(null);
  const draggingRef = useRef(false);

  // Fetch real AQI for realistic values
  useEffect(() => {
    axios.get(`${API}/aqi/comparison?city=Mumbai`)
      .then(r => { setAqiData({ outdoor: r.data.outdoor.aqi, indoor: r.data.indoor.aqi }); })
      .catch(err => logger.error('Slider AQI fetch:', err.message));
  }, []);

  const handleMove = useCallback((clientX) => {
    if (!containerRef.current || !draggingRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pos = Math.max(5, Math.min(95, ((clientX - rect.left) / rect.width) * 100));
    setSliderPos(pos);
  }, []);

  useEffect(() => {
    const onMove = (e) => handleMove(e.touches ? e.touches[0].clientX : e.clientX);
    const onUp = () => { draggingRef.current = false; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onUp); };
  }, [handleMove]);

  // Fixed AQI values from real data — slider only reveals the visual comparison
  const outdoorAqi = aqiData.outdoor;
  const indoorAqi = aqiData.indoor;

  return (
    <section className="section-gap section-surface" data-testid="comparison-section">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-20">
        <SR><p className="overline-glow text-center mb-6">The Difference</p></SR>
        <SR delay={1}><h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center mb-4" style={{ fontFamily: 'Inter' }}>With vs. Without Vayu</h2></SR>
        <SR delay={2}><p className="text-center text-sm mb-12" style={{ color: 'var(--text-muted)' }}>Drag the slider to see the difference</p></SR>
        <SR delay={3}>
          <div ref={containerRef} className="comparison-container relative h-72 md:h-96 max-w-4xl mx-auto" onMouseDown={() => { draggingRef.current = true; }} onTouchStart={() => { draggingRef.current = true; }} data-testid="comparison-slider">
            {/* Without Vayu (full bg) */}
            <div className="absolute inset-0">
              <img src="https://images.unsplash.com/photo-1686840007773-67280673ca0b?w=1000&q=70" alt="Polluted" className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: 'rgba(248,113,113,0.15)' }} />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(6,26,31,0.9) 0%, transparent 60%)' }} />
            </div>
            {/* With Vayu (clipped) */}
            <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${sliderPos}%)` }}>
              <img src="https://images.unsplash.com/photo-1758315417336-38777d626815?w=1000&q=70" alt="Clean" className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: 'rgba(74,222,128,0.05)' }} />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(6,26,31,0.9) 0%, transparent 60%)' }} />
            </div>
            {/* Handle */}
            <div className="absolute top-0 bottom-0 z-10" style={{ left: `${sliderPos}%`, transform: 'translateX(-50%)' }}>
              <div className="w-1 h-full" style={{ background: 'var(--aqua)', boxShadow: '0 0 20px var(--aqua-glow)' }} />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--aqua)', boxShadow: '0 0 25px var(--aqua-glow)', border: '2px solid white' }}>
                <span className="text-white text-xs font-bold">↔</span>
              </div>
            </div>
            {/* Labels */}
            <div className="absolute bottom-6 left-6 z-20">
              <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--warning)' }}>Without Vayu</p>
              <p className="stat-huge text-3xl text-white">AQI {outdoorAqi}</p>
            </div>
            <div className="absolute bottom-6 right-6 z-20 text-right">
              <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--success)' }}>With Vayu</p>
              <p className="stat-huge text-3xl text-white">AQI {indoorAqi}</p>
            </div>
          </div>
        </SR>
      </div>
    </section>
  );
}

// ===== 7. PRODUCT =====
function ProductSection({ onBookDemo }) {
  return (
    <section className="section-gap section-white" data-testid="product-section">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <SR>
            <VayuPaneViewer />
          </SR>
          <div>
            <SR><p className="overline-glow mb-4" style={{ color: 'var(--aqua)' }}>Flagship Product</p></SR>
            <SR delay={1}><h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Inter', color: 'var(--text-dark)' }}>Meet Vayu Pane</h2></SR>
            <SR delay={2}><p className="text-base leading-relaxed mb-8" style={{ color: 'var(--text-secondary-dark)' }}>A smart window that actively purifies air as it enters your home.</p></SR>
            <SR delay={3}>
              <div className="space-y-4 mb-8">
                {['Silent airflow — under 25dB', 'Multi-stage filtration — 99.5% PM2.5', 'Real-time air optimization via Vayu Sense'].map(f => (
                  <div key={f} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--aqua)' }} strokeWidth={1.5} />
                    <span className="text-sm" style={{ color: 'var(--text-secondary-dark)' }}>{f}</span>
                  </div>
                ))}
              </div>
            </SR>
            <SR delay={4}><button onClick={onBookDemo} className="btn-solid flex items-center gap-2" data-testid="product-demo-btn">Book a Demo <ArrowRight className="w-4 h-4" /></button></SR>
          </div>
        </div>
      </div>
    </section>
  );
}

// ===== 8. PROOF =====
function Proof() {
  return (
    <section className="section-gap section-dark relative" data-testid="proof-section">
      <AirParticles count={8} color="rgba(74,222,128,0.06)" />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-20 text-center relative z-10">
        <SR><p className="overline-glow mb-6">Validated Results</p></SR>
        <SR delay={1}><h2 className="stat-huge text-6xl md:text-7xl lg:text-8xl mb-4" style={{ color: 'var(--success)' }} data-testid="proof-number"><CountUp target={90} suffix="%" /></h2></SR>
        <SR delay={2}><p className="text-xl md:text-2xl font-medium mb-4" style={{ fontFamily: 'Inter' }}>reduction in PM2.5</p></SR>
        <SR delay={3}><p className="text-sm max-w-md mx-auto mb-12" style={{ color: 'var(--text-muted)' }}>Prototype tested. Pilot in progress. Real-world data from 8 Indian cities.</p></SR>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
          {[
            { n: 82, s: '%', l: 'Avg. AQI Reduction' },
            { n: 8, s: '', l: 'Cities Active' },
            { n: 99, s: '.5%', l: 'PM2.5 Filtration' },
          ].map(s => (
            <SR key={s.l}>
              <div data-testid={`proof-stat-${s.l}`}>
                <p className="stat-huge text-3xl md:text-4xl mb-1" style={{ color: 'var(--aqua)' }}><CountUp target={s.n} suffix={s.s} /></p>
                <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--text-muted)' }}>{s.l}</p>
              </div>
            </SR>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===== 9. MARKET =====
function Market() {
  return (
    <section className="section-gap section-surface" data-testid="market-section">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-20 text-center">
        <SR><p className="overline-glow mb-6">Market Opportunity</p></SR>
        <SR delay={1}><h2 className="stat-huge text-7xl md:text-8xl lg:text-9xl mb-6" style={{ color: 'var(--aqua)', textShadow: '0 0 80px var(--aqua-glow)' }} data-testid="market-number">$100B+</h2></SR>
        <SR delay={2}><p className="text-xl md:text-2xl font-medium mb-4" style={{ fontFamily: 'Inter' }}>Global indoor air quality market</p></SR>
        <SR delay={3}><p className="text-sm max-w-lg mx-auto" style={{ color: 'var(--text-muted)' }}>Buildings are the last undigitized infrastructure layer. Vayu is building the operating system for air in buildings — hardware, software, and data.</p></SR>
      </div>
    </section>
  );
}

// ===== 10. BUILDERS =====
function Builders({ onBookDemo }) {
  const cards = [
    { icon: Building2, title: 'Integration', desc: 'Installs directly into window frames during construction. Zero structural changes.', color: 'var(--aqua)' },
    { icon: Zap, title: 'Scale', desc: 'From 1 unit to 1,000. Centralized monitoring for entire townships.', color: 'var(--success)' },
    { icon: BarChart3, title: 'Premium Value', desc: 'Buyers pay 8-15% premium for health-focused buildings.', color: '#A78BFA' },
  ];
  return (
    <section className="section-gap section-dark" data-testid="builders-section">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-20">
        <SR><p className="overline-glow text-center mb-6">For Builders</p></SR>
        <SR delay={1}><h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center mb-4" style={{ fontFamily: 'Inter' }}>Designed for modern construction.</h2></SR>
        <SR delay={2}><p className="text-center text-sm mb-16" style={{ color: 'var(--text-muted)' }}>Vayu integrates directly into buildings — enabling developers to deliver healthier living environments at scale.</p></SR>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {cards.map((c, i) => (
            <SR key={c.title} delay={i + 2}>
              <div className="card-dark p-8 h-full" data-testid={`builder-card-${c.title}`}>
                <c.icon className="w-8 h-8 mb-5" style={{ color: c.color }} strokeWidth={1.5} />
                <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Inter' }}>{c.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary-light)' }}>{c.desc}</p>
              </div>
            </SR>
          ))}
        </div>
        <SR delay={5}>
          <div className="card-dark p-6 max-w-3xl mx-auto mb-12 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--success)' }}>Green Certification</p>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary-light)' }}>Vayu systems are designed to support indoor air quality strategies aligned with global green building standards such as LEED, IGBC, and GRIHA.</p>
          </div>
        </SR>
        <SR delay={5}><div className="text-center"><button onClick={onBookDemo} className="btn-glow inline-flex items-center gap-2" data-testid="builder-cta-btn">Partner With Us <ArrowRight className="w-4 h-4" /></button></div></SR>
      </div>
    </section>
  );
}

// ===== 11. VISION =====
function Vision() {
  return (
    <section className="section-gap relative overflow-hidden" style={{ background: 'linear-gradient(180deg, var(--bg-deep) 0%, #040E12 100%)' }} data-testid="vision-section">
      <AirParticles count={15} color="rgba(63,167,214,0.05)" />
      <div className="max-w-[800px] mx-auto px-6 lg:px-20 text-center relative z-10">
        <SR>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-8" style={{ fontFamily: 'Inter' }} data-testid="vision-headline">
            Buildings should<br /><span style={{ color: 'var(--aqua)' }}>breathe.</span>
          </h2>
        </SR>
        <SR delay={1}><p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: 'var(--text-secondary-light)' }}>We believe buildings should not just shelter us — they should actively protect us.</p></SR>
        <SR delay={2}><p className="text-sm" style={{ color: 'var(--text-muted)' }}>Jagdale Air is building the operating system for air in buildings.</p></SR>
      </div>
    </section>
  );
}

// ===== 12. CTA =====
function FinalCTA({ onBookDemo }) {
  const navigate = useNavigate();
  return (
    <section className="section-gap section-surface relative" data-testid="final-cta">
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 50% 50%, rgba(63,167,214,0.06) 0%, transparent 70%)' }} />
      <div className="max-w-[800px] mx-auto px-6 lg:px-20 text-center relative z-10">
        <SR><h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Inter' }}>Experience the future<br />of air.</h2></SR>
        <SR delay={1}>
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <button onClick={onBookDemo} className="btn-glow flex items-center gap-2 text-base px-10 py-4" data-testid="cta-demo-btn">Book a Demo <ArrowRight className="w-4 h-4" /></button>
            <Link to="/contact" className="btn-ghost text-base px-10 py-4" data-testid="cta-access-btn">Get Early Access</Link>
          </div>
        </SR>
      </div>
    </section>
  );
}

// ===== CITY AQI (KEPT) =====
function CityAqi() {
  const [cities, setCities] = useState([]);
  useEffect(() => {
    axios.get(`${API}/aqi/cities`).then(r => setCities(r.data.cities || [])).catch(err => logger.error('Cities fetch:', err.message));
  }, []);
  return (
    <section className="section-gap section-dark relative overflow-hidden" data-testid="city-aqi-section">
      <AirParticles count={10} color="rgba(63,167,214,0.06)" />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-20 relative z-10">
        <SR><p className="overline-glow text-center mb-6">Live Air Quality</p></SR>
        <SR delay={1}><h2 className="text-3xl md:text-4xl font-bold tracking-tight text-center mb-12" style={{ fontFamily: 'Inter' }}>Air Quality Across India</h2></SR>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cities.map((city) => (
            <SR key={city.name}>
              <div className="rounded-2xl p-5 text-center" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }} data-testid={`city-${city.name}`}>
                <p className="text-sm font-medium mb-3" style={{ fontFamily: 'Inter' }}>{city.name}</p>
                <div className="flex justify-center items-center gap-3 mb-2">
                  <div className="text-center"><span className="stat-huge text-lg" style={{ color: getAqiColor(city.aqi) }}>{city.aqi || '--'}</span><p className="text-[8px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Out</p></div>
                  <ArrowRight className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                  <div className="text-center"><span className="stat-huge text-lg" style={{ color: 'var(--success)' }}>{city.indoor_aqi || '--'}</span><p className="text-[8px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Vayu</p></div>
                </div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: 'rgba(74,222,128,0.1)', color: 'var(--success)' }}>-{city.reduction || 0}%</span>
              </div>
            </SR>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===== HOME =====
export default function Home({ onBookDemo }) {
  return (
    <div>
      <Hero onBookDemo={onBookDemo} />
      <Problem />
      <Breakthrough />
      <Solution />
      <HowItWorks />
      <WithVsWithout />
      <AirFlowSimulator />
      <ProductSection onBookDemo={onBookDemo} />
      <Proof />
      <CityAqi />
      <Builders onBookDemo={onBookDemo} />
      <Vision />
      <FinalCTA onBookDemo={onBookDemo} />
    </div>
  );
}
